import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  message,
  Tooltip,
  Checkbox,
  Radio,
  Select,
  Form,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';

import ModalWrapper from './ModalWrapper';
import LabelPickerTray from './LabelPickerTray';
import { captureAnalytics } from '../../actions/preload';
import './addLabelModal.scss';

const { Option } = Select;
const NO_LABEL_CORRECTION = 'No label should be applied';

class AddLabelToRecord extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sendTokens: props.modalType.data.record,
      receiverType: props.modalType.data.receiverType,
      labels: [],
      selectedLabels: [],
      searchValue: '',
      isDocumentWronglyLabeled: false,
      correctionOutcome: null,
      correctLabel: null,
      correctLabelValidateStatus: '',
      help: '',
    };
  }

  componentDidMount = async () => {
    // eslint-disable-next-line react/destructuring-assignment
    const page = 1;
    const pageSize = 'max';
    // Surface the labels this user applies most (last 30 days) at the top.
    const sort = 'user_label_usage';
    const { getLabelsByPage } = this.props;
    await getLabelsByPage(page, pageSize, sort);
    const { data, selectedRecord, modalType } = this.props;
    const initialSelectedLabels = modalType.data.selectedLabelsRecordViewer
      ? modalType.data.selectedLabelsRecordViewer
      : selectedRecord[0].labels;
    this.setState({
      labels: data,
      selectedLabels: [...initialSelectedLabels],
    });
  }

  selectLabel = (label) => {
    const { selectedLabels } = this.state;
    const currentlySelected = [...selectedLabels];
    const indexOfSelectedLabel = currentlySelected.findIndex(selectedLabel => selectedLabel.name === label.name);
    const labelAlreadySelected = currentlySelected.find(selectedLabel => selectedLabel.name === label.name);
    if (!labelAlreadySelected) {
      this.setState({
        selectedLabels: [...currentlySelected, label],
      });
    } else {
      currentlySelected.splice(indexOfSelectedLabel, 1);
      this.setState({
        selectedLabels: currentlySelected,
      });
    }
  }

  deselectLabel = (label, index) => {
    const { selectedLabels } = this.state;
    const currentlySelected = [...selectedLabels];
    currentlySelected.splice(index, 1);
    this.setState({
      selectedLabels: currentlySelected,
    });
  }

  update = async (event) => {
    event.preventDefault();
    const {
      sendTokens,
      selectedLabels,
      receiverType,
      isDocumentWronglyLabeled,
      correctionOutcome,
      correctLabel,
    } = this.state;
    if (isDocumentWronglyLabeled && !correctionOutcome) {
      this.setState({
        correctLabelValidateStatus: 'error',
        help: 'Choose what should have happened',
      });
      return;
    }
    if (isDocumentWronglyLabeled && correctionOutcome === 'replacement_label' && !correctLabel) {
      this.setState({
        correctLabelValidateStatus: 'error',
        help: 'Please select the correct label',
      });
      return;
    }
    const { editReceivedRecordLabels } = this.props;
    const labelsToSave = correctionOutcome === 'no_label' ? [] : selectedLabels;
    const selectedLabelIds = labelsToSave.map(label => label.id);
    const reportedCorrection = correctionOutcome === 'no_label' ? NO_LABEL_CORRECTION : correctLabel;
    await editReceivedRecordLabels(
      sendTokens,
      selectedLabelIds,
      labelsToSave,
      receiverType,
      isDocumentWronglyLabeled,
      reportedCorrection,
    );

    const { updatedLabel, hideModal } = this.props;
    if (updatedLabel) {
      hideModal();
      message.success({
        content: 'Labels changed',
      });
      this.updateRowSelected(labelsToSave);
    }
  }

  updateRowSelected(selectedLabels = this.state.selectedLabels) {
    const { modalType, rowSelected, selectedRecord } = this.props;
    const target = modalType.data.record;
    const token = typeof target === 'string' ? target : target?.send_token;
    const selectedRow = selectedRecord.find((record) => record.send_token === token)
      || (target && typeof target === 'object' && !Array.isArray(target) ? target : null);
    if (!selectedRow) return;

    // Preserve workflow identity and capabilities for the next action. A label
    // edit must not turn a redacted Reply into an untyped ordinary selection.
    rowSelected([{ ...selectedRow, labels: selectedLabels }]);
  }

  renderLabelIncorrectCheckBox = () => {
    const { isDocumentWronglyLabeled } = this.state;
    return (
      <div className="add-label-modal__report">
        <Checkbox
          checked={isDocumentWronglyLabeled}
          onChange={() => this.setState((prevState) => {
            const isChecked = !prevState.isDocumentWronglyLabeled;
            captureAnalytics('ai_label_error_report_toggled', { checked: isChecked });
            return {
              isDocumentWronglyLabeled: isChecked,
              correctionOutcome: isChecked ? 'replacement_label' : null,
              correctLabel: null,
              correctLabelValidateStatus: '',
              help: '',
            };
          })}
          disabled={false}
        >
          Has document been wrongly labeled?
        </Checkbox>
        <Tooltip
          title="Reporting errors with our AI Labeling will help us train our model and become more accurate in the future"
        >
          <QuestionCircleOutlined className="add-label-modal__report-help" />
        </Tooltip>
      </div>
    );
  }

  renderModal = () => {
    const { isLoading, hideModal: handleHideModal } = this.props;
    const {
      labels, selectedLabels, searchValue, isDocumentWronglyLabeled,
    } = this.state;
    const { modalType: { data: { reportAiDetails } } } = this.props;
    const { isLabelEdited, hasWrongLabelingBeenReported, aiStatus } = reportAiDetails;
    const hasDocumentBeenClassified = !!aiStatus;
    if (isLoading) {
      return (<span> Updating labels, Please wait... </span>);
    }
    const maxAdditionalLabels = 5 - selectedLabels.length;
    return (
      <div className="add-label-modal">
        <LabelPickerTray
          labels={labels}
          maxAdditionalLabels={maxAdditionalLabels}
          selectedLabels={selectedLabels}
          selectedTitle="Current labels"
          emptySelectedText="No labels selected yet"
          searchValue={searchValue}
          onSearchChange={(value) => this.setState({ searchValue: value })}
          onSelectLabel={this.selectLabel}
          onDeselectLabel={this.deselectLabel}
          onClearLabels={() => this.setState({ selectedLabels: [] })}
          onManageLabelsClick={handleHideModal}
          showClear={true}
        />

        {hasDocumentBeenClassified && !isLabelEdited && !hasWrongLabelingBeenReported && this.renderLabelIncorrectCheckBox()}
        {isDocumentWronglyLabeled && this.renderCorrectLabelInput()}
      </div>
    );
  }

  handleChangeSelect = (value) => {
    captureAnalytics('ai_label_correction_outcome_selected', {
      outcome: 'replacement_label',
    });
    return this.setState({
      correctLabel: value,
      correctLabelValidateStatus: '',
      help: '',
    });
  }

  handleCorrectionOutcomeChange = (event) => {
    const correctionOutcome = event.target.value;
    const { correctLabel } = this.state;
    captureAnalytics('ai_label_correction_outcome_selected', {
      outcome: correctionOutcome,
    });
    this.setState({
      correctionOutcome,
      correctLabel: correctionOutcome === 'replacement_label' ? correctLabel : null,
      correctLabelValidateStatus: '',
      help: '',
    });
  }

  renderCorrectLabelInput = () => {
    const {
      labels, correctionOutcome, correctLabel, correctLabelValidateStatus, help,
    } = this.state;
    return (
      <Form.Item
        label="What should have happened?"
        required
        validateStatus={correctLabelValidateStatus}
        help={help}
      >
        <Radio.Group
          className="add-label-modal__correction-options"
          value={correctionOutcome}
          onChange={this.handleCorrectionOutcomeChange}
        >
          <Radio value="replacement_label">A different label should be applied</Radio>
          <div className="add-label-modal__correct-label-control">
            <Select
              showSearch
              aria-label="Correct label"
              placeholder="Select the correct label"
              optionFilterProp="children"
              value={correctLabel || undefined}
              disabled={correctionOutcome !== 'replacement_label'}
              onChange={(value) => this.handleChangeSelect(value)}
              className="add-label-modal__correct-label-select"
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {labels.map((label) => <Option key={label.id} value={label.name}>{label.name}</Option>)}
            </Select>
          </div>
          <Radio value="no_label">No label should be applied</Radio>
        </Radio.Group>
      </Form.Item>
    );
  }

  render() {
    const { isLoading, hideModal } = this.props;
    const customContentStyle = {
      width: '460px',
    };

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        width="460px"
        wrapClassName="add-label-modal-wrap"
        action={this.update}
        actionName="Submit"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={isLoading}
        modalTitle="Add or edit labels"
        form="addLabelToRecord"
      >
        <Form
          layout="vertical"
          onFinish={this.update}
          id="addLabelToRecord"
        >
          {this.renderModal()}
        </Form>
      </ModalWrapper>
    );
  }
}

AddLabelToRecord.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  rowSelected: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  updatedLabel: PropTypes.bool.isRequired,
  editReceivedRecordLabels: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
  selectedRecord: PropTypes.array.isRequired,
  getLabelsByPage: PropTypes.func.isRequired,
};

export default connect(state => ({
  isLoading: state.records.isLoading,
  emailValidMessage: state.records.emailValidMessage,
  updatedLabel: state.records.updatedLabel,
  data: state.labels.data,
  selectedRecord: state.inboxTable.recordData,
}))(AddLabelToRecord);
